import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, TextField, Button, Alert, CircularProgress, Chip, Stack, InputAdornment, Autocomplete, Divider, FormControlLabel, Switch, Checkbox, FormGroup } from '@mui/material';
import api, { settingsApi, unwrapApiResponse } from '../services/api';
import type { LandingPromo } from '../types';

/** Kate Mobile app id — у него уже есть video/offline; свои приложения часто в это не пускают. */
const KATE_MOBILE_CLIENT_ID = '2685278';
const KATE_AUTH_URL =
  `https://oauth.vk.com/authorize?client_id=${KATE_MOBILE_CLIENT_ID}`
  + '&display=page'
  + '&redirect_uri=https://oauth.vk.com/blank.html'
  + '&scope=video,offline'
  + '&response_type=token'
  + '&v=5.199'
  + '&revoke=1';

type VkStatus = {
  isReady: boolean;
  isUserIdSet: boolean;
  isAccessTokenSet: boolean;
  isRefreshConfigured: boolean;
  userId: number | null;
  clientId: string | null;
  redirectUri: string | null;
  lastAccessTokenUpdate: string | null;
  lastUserIdUpdate: string | null;
  lastRefreshTokenUpdate: string | null;
  accessExpiresAt: string | null;
  mode: string;
};

function parseKateRedirect(raw: string): { accessToken: string; userId: number | null; expiresIn: string | null } {
  const text = raw.trim();
  let hash = text;
  const hashIdx = text.indexOf('#');
  if (hashIdx >= 0) {
    hash = text.slice(hashIdx + 1);
  } else if (text.includes('access_token=')) {
    hash = text.includes('?') ? text.split('?')[1] : text;
  }
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const bareToken = !params.get('access_token')
    && ((text.startsWith('vk1.') || (/^[a-zA-Z0-9._-]+$/.test(text) && text.length > 40)));
  if (bareToken) {
    return { accessToken: text, userId: null, expiresIn: null };
  }
  const accessToken = params.get('access_token');
  if (!accessToken) {
    throw new Error('В ссылке нет access_token. Скопируйте весь URL после «Разрешить» (от https://oauth.vk.com/blank.html#...)');
  }
  const userIdRaw = params.get('user_id');
  return {
    accessToken,
    userId: userIdRaw ? Number(userIdRaw) : null,
    expiresIn: params.get('expires_in'),
  };
}

const SystemPage: React.FC = () => {
  const [katePaste, setKatePaste] = useState('');
  const [vkSecret, setVkSecret] = useState('');
  const [vkRefresh, setVkRefresh] = useState('');
  const [vkDeviceId, setVkDeviceId] = useState('');
  const [vkClientId, setVkClientId] = useState('');
  const [vkUserId, setVkUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [savingKate, setSavingKate] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [vkStatus, setVkStatus] = useState<VkStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const [cacheName, setCacheName] = useState('');
  const [cacheNames, setCacheNames] = useState<string[]>([]);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheSuccess, setCacheSuccess] = useState<string | null>(null);
  const [cacheError, setCacheError] = useState<string | null>(null);
  const [loadingNames, setLoadingNames] = useState(true);

  const [testMode, setTestMode] = useState(false);
  const [testModeLoading, setTestModeLoading] = useState(true);
  const [testModeSaving, setTestModeSaving] = useState(false);
  const [testModeError, setTestModeError] = useState<string | null>(null);
  const [testModeAvailable, setTestModeAvailable] = useState(true);

  const [autoMode, setAutoMode] = useState(false);
  const [autoModeLoading, setAutoModeLoading] = useState(true);
  const [autoModeSaving, setAutoModeSaving] = useState(false);
  const [autoModeError, setAutoModeError] = useState<string | null>(null);
  const [autoModeInfo, setAutoModeInfo] = useState<string | null>(null);
  const [autoModeAvailable, setAutoModeAvailable] = useState(true);

  const [promoEnabled, setPromoEnabled] = useState(false);
  const [promoFreeBusiness, setPromoFreeBusiness] = useState(false);
  const [promoDiscountStart, setPromoDiscountStart] = useState(false);
  const [promoDiscountBusiness, setPromoDiscountBusiness] = useState(false);
  const [promoPercent, setPromoPercent] = useState(50);
  const [promoLoading, setPromoLoading] = useState(true);
  const [promoSaving, setPromoSaving] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoSuccess, setPromoSuccess] = useState<string | null>(null);
  const [promoAvailable, setPromoAvailable] = useState(true);

  const fetchTestMode = async () => {
    setTestModeLoading(true);
    try {
      const res = await settingsApi.getTestMode();
      setTestMode(!!res.enabled);
      setTestModeError(null);
      setTestModeAvailable(true);
    } catch {
      setTestModeAvailable(false);
      setTestModeError(null);
    } finally {
      setTestModeLoading(false);
    }
  };

  const fetchAutoMode = async () => {
    setAutoModeLoading(true);
    try {
      const res = await settingsApi.getAutoMode();
      setAutoMode(!!res.enabled);
      setAutoModeError(null);
      setAutoModeAvailable(true);
    } catch {
      setAutoModeAvailable(false);
      setAutoModeError(null);
    } finally {
      setAutoModeLoading(false);
    }
  };

  const handleTestModeChange = async (enabled: boolean) => {
    setTestModeSaving(true);
    setTestModeError(null);
    try {
      const res = await settingsApi.setTestMode(enabled);
      setTestMode(!!res.enabled);
      setTestModeAvailable(true);
    } catch {
      setTestModeError('Не удалось сохранить настройку');
    } finally {
      setTestModeSaving(false);
    }
  };

  const handleAutoModeChange = async (enabled: boolean) => {
    if (enabled && !window.confirm(
      'Включить автоматический режим?\n\nСдача будет запускаться только 1-го числа каждого месяца по расписанию.\nПри включении цикл сразу не стартует.\n\nПри ошибках можно выключить и сдать вручную.'
    )) {
      return;
    }
    setAutoModeSaving(true);
    setAutoModeError(null);
    setAutoModeInfo(null);
    try {
      const res = await settingsApi.setAutoMode(enabled);
      setAutoMode(!!res.enabled);
      setAutoModeAvailable(true);
      if (enabled) {
        setAutoModeInfo('Авторежим включён. Следующий запуск — 1-го числа по расписанию.');
      }
    } catch {
      setAutoModeError('Не удалось сохранить настройку');
    } finally {
      setAutoModeSaving(false);
    }
  };

  const applyPromo = (p: LandingPromo) => {
    setPromoEnabled(!!p.enabled);
    setPromoFreeBusiness(!!p.businessFirstMonthFree);
    setPromoDiscountStart(!!p.discountStart);
    setPromoDiscountBusiness(!!p.discountBusiness);
    setPromoPercent(p.discountPercent > 0 ? p.discountPercent : 50);
  };

  const fetchLandingPromo = async () => {
    setPromoLoading(true);
    try {
      const res = await settingsApi.getLandingPromo();
      applyPromo(res);
      setPromoError(null);
      setPromoAvailable(true);
    } catch {
      setPromoAvailable(false);
      setPromoError(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const saveLandingPromo = async (next?: Partial<LandingPromo>) => {
    const payload: LandingPromo = {
      enabled: next?.enabled ?? promoEnabled,
      businessFirstMonthFree: next?.businessFirstMonthFree ?? promoFreeBusiness,
      discountStart: next?.discountStart ?? promoDiscountStart,
      discountBusiness: next?.discountBusiness ?? promoDiscountBusiness,
      discountPercent: next?.discountPercent ?? promoPercent,
    };
    if (payload.enabled
        && !payload.businessFirstMonthFree
        && !payload.discountStart
        && !payload.discountBusiness) {
      setPromoError('Выберите хотя бы один вариант акции');
      return;
    }
    if (payload.discountPercent < 1 || payload.discountPercent > 99) {
      setPromoError('Скидка должна быть от 1 до 99%');
      return;
    }
    setPromoSaving(true);
    setPromoError(null);
    setPromoSuccess(null);
    try {
      const res = await settingsApi.setLandingPromo(payload);
      applyPromo(res);
      setPromoSuccess(res.enabled ? 'Акция включена — лендинг обновлён' : 'Акция выключена');
      setPromoAvailable(true);
    } catch (e: any) {
      setPromoError(e?.response?.data?.message || e?.message || 'Не удалось сохранить акцию');
    } finally {
      setPromoSaving(false);
    }
  };

  const togglePromoFlag = (key: 'businessFirstMonthFree' | 'discountStart' | 'discountBusiness', checked: boolean) => {
    const next: Partial<LandingPromo> = { [key]: checked };
    if (key === 'businessFirstMonthFree') setPromoFreeBusiness(checked);
    if (key === 'discountStart') setPromoDiscountStart(checked);
    if (key === 'discountBusiness') setPromoDiscountBusiness(checked);
    if (promoEnabled) {
      void saveLandingPromo(next);
    }
  };

  const fetchStatus = async () => {
    setStatusLoading(true);
    try {
      const res = await unwrapApiResponse<VkStatus>(api.get('/vkUserActor/status'));
      setVkStatus(res);
      if (res?.clientId && !vkClientId) {
        setVkClientId(res.clientId);
      }
    } catch {
      setVkStatus(null);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchTestMode();
    fetchAutoMode();
    fetchLandingPromo();
    const params = new URLSearchParams(window.location.search);
    const vk = params.get('vk');
    const msg = params.get('vk_message');
    if (vk === 'connected') {
      setSuccess('VK подключён через VK ID OAuth.');
      window.history.replaceState({}, '', '/system');
    } else if (vk === 'error') {
      setError(msg || 'Ошибка подключения VK');
      window.history.replaceState({}, '', '/system');
    }
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCacheNames = async () => {
      try {
        setLoadingNames(true);
        const res = await unwrapApiResponse<string[]>(api.get('/cache/names'));
        setCacheNames(res);
      } catch {
        setCacheNames([]);
      } finally {
        setLoadingNames(false);
      }
    };
    fetchCacheNames();
  }, []);

  const canSave =
    vkSecret.trim() !== ''
    || vkRefresh.trim() !== ''
    || vkDeviceId.trim() !== ''
    || vkClientId.trim() !== ''
    || vkUserId.trim() !== '';

  const handleOpenKate = () => {
    window.open(KATE_AUTH_URL, '_blank', 'noopener,noreferrer');
  };

  const handleSaveKate = async () => {
    setSavingKate(true);
    setSuccess(null);
    setError(null);
    try {
      const parsed = parseKateRedirect(katePaste);
      await api.post('/vkUserActor', {
        accessToken: parsed.accessToken,
        userId: parsed.userId,
        refreshToken: null,
        deviceId: null,
        clientId: KATE_MOBILE_CLIENT_ID,
      });
      const eternal = parsed.expiresIn === '0' || parsed.expiresIn == null;
      setSuccess(
        eternal
          ? `Токен Kate Mobile сохранён (user_id=${parsed.userId ?? 'не указан'}). Бессрочный — брать каждый раз не нужно.`
          : `Токен сохранён (expires_in=${parsed.expiresIn}). Если протухнет — выдайте ещё раз через Kate.`
      );
      setKatePaste('');
      fetchStatus();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось сохранить токен Kate');
    } finally {
      setSavingKate(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setSuccess(null);
    setError(null);
    try {
      const data = await unwrapApiResponse<{ authorizeUrl: string; redirectUri: string }>(
        api.post('/vkUserActor/oauth/start', {
          clientId: vkClientId.trim() || null,
        })
      );
      if (!data?.authorizeUrl) {
        throw new Error('Не получен authorizeUrl');
      }
      window.location.href = data.authorizeUrl;
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось начать OAuth');
      setConnecting(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    setSuccess(null);
    setError(null);
    try {
      await api.post('/vkUserActor', {
        accessToken: vkSecret.trim() === '' ? null : vkSecret.trim(),
        refreshToken: vkRefresh.trim() === '' ? null : vkRefresh.trim(),
        deviceId: vkDeviceId.trim() === '' ? null : vkDeviceId.trim(),
        clientId: vkClientId.trim() === '' ? null : vkClientId.trim(),
        userId: vkUserId.trim() === '' ? null : Number(vkUserId.trim()),
      });
      setSuccess('VK credentials сохранены.');
      setVkSecret('');
      setVkRefresh('');
      setVkDeviceId('');
      setVkUserId('');
      fetchStatus();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Ошибка при сохранении VK credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setSuccess(null);
    setError(null);
    try {
      await unwrapApiResponse(api.post('/vkUserActor/refresh'));
      setSuccess('Access token обновлён через refresh_token');
      fetchStatus();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось обновить токен');
    } finally {
      setRefreshing(false);
    }
  };

  const handleClearCache = async () => {
    if (!cacheName) return;
    setCacheLoading(true);
    setCacheSuccess(null);
    setCacheError(null);
    try {
      await api.post(`/cache/${cacheName}/clear`);
      setCacheSuccess(`Кэш "${cacheName}" успешно очищен!`);
    } catch {
      setCacheError('Ошибка при очистке кэша');
    } finally {
      setCacheLoading(false);
    }
  };

  const modeLabel = (mode?: string) => {
    if (mode === 'AUTO_REFRESH') return 'Автообновление';
    if (mode === 'STATIC_ACCESS') return 'Статический access';
    return 'Не настроено';
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1.5 }}>
        Система
      </Typography>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems="flex-start"
        sx={{ maxWidth: '100%', minWidth: 0 }}
      >
        <Card sx={{ maxWidth: { xs: '100%', md: 560 }, width: '100%', minWidth: 0, overflow: 'hidden' }}>
          <CardContent sx={{ minWidth: 0 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">
                VK Video: токен
              </Typography>
              {vkStatus && (
                <Chip
                  size="small"
                  label={modeLabel(vkStatus.mode)}
                  color={vkStatus.mode === 'AUTO_REFRESH' || vkStatus.isReady ? 'success' : 'default'}
                />
              )}
            </Stack>

            <Alert severity="success" sx={{ mb: 2 }}>
              Рекомендуемый способ (как у вас с TG→VK): токен через <b>Kate Mobile</b>.
              У Kate уже есть права video+offline; свои приложения часто «не пускают».
              Один раз выдали → сохранили → для сбора просмотров хватает надолго (expires_in=0).
            </Alert>

            <Button variant="contained" onClick={handleOpenKate} fullWidth sx={{ mb: 1 }}>
              1. Открыть авторизацию Kate Mobile
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Нажмите «Разрешить» → скопируйте весь URL из адресной строки
              (https://oauth.vk.com/blank.html#access_token=...&user_id=...&expires_in=0) и вставьте ниже.
            </Typography>
            <TextField
              label="Ссылка blank.html или сам access_token"
              value={katePaste}
              onChange={e => setKatePaste(e.target.value)}
              fullWidth
              margin="dense"
              multiline
              minRows={2}
              placeholder="https://oauth.vk.com/blank.html#access_token=vk1.a...."
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveKate}
              disabled={savingKate || !katePaste.trim()}
              fullWidth
              sx={{ mt: 1, mb: 2 }}
            >
              {savingKate ? <CircularProgress size={20} /> : '2. Сохранить токен'}
            </Button>

            {vkStatus?.isAccessTokenSet && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 2, overflowWrap: 'anywhere' }}
              >
                Сейчас: user_id={vkStatus.userId ?? '—'}, режим={modeLabel(vkStatus.mode)}
                {vkStatus.lastAccessTokenUpdate
                  ? `, обновлён ${vkStatus.lastAccessTokenUpdate}`
                  : ''}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Альтернатива: своё приложение VK ID (сложнее)
            </Typography>
            <TextField
              label="client_id своего приложения"
              value={vkClientId}
              onChange={e => setVkClientId(e.target.value)}
              fullWidth
              margin="dense"
              size="small"
            />
            <Button
              variant="outlined"
              onClick={handleConnect}
              disabled={connecting || !vkClientId.trim()}
              fullWidth
              sx={{ mt: 1, mb: 2 }}
            >
              {connecting ? <CircularProgress size={20} /> : 'Подключить через VK ID OAuth'}
            </Button>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Ручные поля (редко нужно)
            </Typography>
            <TextField
              label="access_token"
              value={vkSecret}
              onChange={e => setVkSecret(e.target.value)}
              fullWidth
              margin="dense"
              size="small"
              type="password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {statusLoading ? <CircularProgress size={16} /> : vkStatus && (
                      <Chip
                        label={vkStatus.isAccessTokenSet ? 'есть' : 'нет'}
                        color={vkStatus.isAccessTokenSet ? 'success' : 'warning'}
                        size="small"
                      />
                    )}
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label="user_id"
              value={vkUserId}
              onChange={e => setVkUserId(e.target.value)}
              fullWidth
              margin="dense"
              size="small"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
              <Button variant="text" onClick={handleUpdate} disabled={loading || !canSave} fullWidth>
                {loading ? <CircularProgress size={18} /> : 'Сохранить вручную'}
              </Button>
              <Button
                variant="text"
                onClick={handleRefresh}
                disabled={refreshing || !vkStatus?.isRefreshConfigured}
                fullWidth
              >
                {refreshing ? <CircularProgress size={18} /> : 'Refresh VK ID'}
              </Button>
            </Stack>
            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </CardContent>
        </Card>
        <Stack spacing={2} sx={{ maxWidth: { xs: '100%', md: 400 }, flex: 1, width: '100%', minWidth: 0 }}>
          <Card sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
            <CardContent sx={{ minWidth: 0 }}>
              <Typography variant="h6" gutterBottom>
                Очистка кэша
              </Typography>
              <Autocomplete
                options={cacheNames}
                loading={loadingNames}
                value={cacheName}
                onChange={(_, value) => setCacheName(value || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Имя кэша"
                    placeholder="Выберите кэш для очистки"
                    fullWidth
                    margin="normal"
                    InputProps={{ ...params.InputProps, endAdornment: loadingNames ? <CircularProgress size={20} /> : params.InputProps.endAdornment }}
                  />
                )}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleClearCache}
                disabled={cacheLoading || !cacheName}
                fullWidth
                sx={{ mt: 2 }}
              >
                {cacheLoading ? <CircularProgress size={20} /> : 'Очистить кэш'}
              </Button>
              {cacheSuccess && <Alert severity="success" sx={{ mt: 2 }}>{cacheSuccess}</Alert>}
              {cacheError && <Alert severity="error" sx={{ mt: 2 }}>{cacheError}</Alert>}
            </CardContent>
          </Card>
          <Card sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
            <CardContent sx={{ minWidth: 0 }}>
              <Typography variant="h6" gutterBottom>
                Автоматический режим
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Только включает расписание: сдача идёт 1-го числа каждого месяца
                (акты и статистика за прошлый месяц). При включении цикл сразу не стартует.
                При ошибках выключите и проверьте/отправьте вручную.
              </Typography>
              {autoModeLoading ? (
                <CircularProgress size={22} />
              ) : !autoModeAvailable ? (
                <Alert severity="info">
                  Настройка станет доступна после перезапуска backend.
                </Alert>
              ) : (
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoMode}
                      disabled={autoModeSaving}
                      onChange={(_, checked) => handleAutoModeChange(checked)}
                    />
                  }
                  label={autoMode ? 'Включён' : 'Выключен'}
                />
              )}
              {autoModeInfo && <Alert severity="success" sx={{ mt: 1.5 }}>{autoModeInfo}</Alert>}
              {autoModeError && <Alert severity="error" sx={{ mt: 1.5 }}>{autoModeError}</Alert>}
            </CardContent>
          </Card>
          <Card sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
            <CardContent sx={{ minWidth: 0 }}>
              <Typography variant="h6" gutterBottom>
                Тестовая среда
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Включите для ручной сдачи актов и статистики по отдельным креативам.
              </Typography>
              {testModeLoading ? (
                <CircularProgress size={22} />
              ) : !testModeAvailable ? (
                <Alert severity="info">
                  Настройка станет доступна после перезапуска backend.
                </Alert>
              ) : (
                <FormControlLabel
                  control={
                    <Switch
                      checked={testMode}
                      disabled={testModeSaving}
                      onChange={(_, checked) => handleTestModeChange(checked)}
                    />
                  }
                  label={testMode ? 'Включена' : 'Выключена'}
                />
              )}
              {testModeError && <Alert severity="error" sx={{ mt: 1.5 }}>{testModeError}</Alert>}
            </CardContent>
          </Card>
          <Card sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
            <CardContent sx={{ minWidth: 0 }}>
              <Typography variant="h6" gutterBottom>
                Акция на главной
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Включите акцию — на лендинге появятся спеццены на первый месяц.
                Можно комбинировать: например, бесплатный «Бизнес» и скидка на «Старт».
              </Typography>
              {promoLoading ? (
                <CircularProgress size={22} />
              ) : !promoAvailable ? (
                <Alert severity="info">
                  Настройка станет доступна после перезапуска backend.
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={promoEnabled}
                        disabled={promoSaving}
                        onChange={(_, checked) => {
                          setPromoEnabled(checked);
                          void saveLandingPromo({ enabled: checked });
                        }}
                      />
                    }
                    label={promoEnabled ? 'Акция включена' : 'Акция выключена'}
                  />
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={promoFreeBusiness}
                          disabled={promoSaving}
                          onChange={(_, checked) => togglePromoFlag('businessFirstMonthFree', checked)}
                        />
                      }
                      label="Первый месяц на тарифе «Бизнес» бесплатно"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={promoDiscountStart}
                          disabled={promoSaving}
                          onChange={(_, checked) => togglePromoFlag('discountStart', checked)}
                        />
                      }
                      label={`Скидка ${promoPercent}% на тариф «Старт»`}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={promoDiscountBusiness}
                          disabled={promoSaving || promoFreeBusiness}
                          onChange={(_, checked) => togglePromoFlag('discountBusiness', checked)}
                        />
                      }
                      label={`Скидка ${promoPercent}% на тариф «Бизнес»`}
                    />
                  </FormGroup>
                  {(promoDiscountStart || promoDiscountBusiness) && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        label="Размер скидки, %"
                        type="number"
                        size="small"
                        value={promoPercent}
                        disabled={promoSaving}
                        onChange={(e) => setPromoPercent(Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
                        inputProps={{ min: 1, max: 99 }}
                        sx={{ width: 160 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={promoSaving}
                        onClick={() => saveLandingPromo({ discountPercent: promoPercent })}
                      >
                        Сохранить %
                      </Button>
                    </Stack>
                  )}
                </Stack>
              )}
              {promoSuccess && <Alert severity="success" sx={{ mt: 1.5 }}>{promoSuccess}</Alert>}
              {promoError && <Alert severity="error" sx={{ mt: 1.5 }}>{promoError}</Alert>}
            </CardContent>
          </Card>
        </Stack>
      </Stack>
    </Box>
  );
};

export default SystemPage;
